/**
 * manager-search.js
 * Gerencia busca preditiva com dois modos de UI.
 * Disponível como window.SearchManager.
 *
 * ─── Modo drawer ──────────────────────────────────────────────────────────
 *
 *   O SearchManager fica DENTRO do elemento data-drawer="search".
 *   A abertura/fechamento é controlada pelo DrawerManager.
 *
 *   <!-- section search-drawer.liquid -->
 *   <div data-drawer="search" data-drawer-side="right">
 *     <div data-drawer-overlay></div>
 *     <div data-search data-search-mode="drawer">
 *       <input data-search-input type="search">
 *       <button data-search-clear hidden>×</button>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Modo dropdown ────────────────────────────────────────────────────────
 *
 *   O SearchManager fica no header. O painel de resultados abre abaixo do input.
 *
 *   <div data-search data-search-mode="dropdown">
 *     <input data-search-input type="search">
 *     <button data-search-clear hidden>×</button>
 *     <div data-search-dropdown hidden>
 *       <div data-search-pre-search>...</div>
 *       <div data-search-results></div>
 *     </div>
 *   </div>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   data-search                → container raiz
 *   data-search-mode           → "drawer" | "dropdown"
 *   data-search-input          → campo de busca
 *   data-search-results        → container de resultados
 *   data-search-pre-search     → estado pré-busca (visível quando input vazio)
 *   data-search-clear          → botão limpar input
 *   data-search-dropdown       → painel dropdown (modo dropdown apenas)
 *   data-search-result         → item de resultado (gerado pelo manager)
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.SearchManager.open()
 *   window.SearchManager.close()
 *   window.SearchManager.toggle()
 *   window.SearchManager.destroy()
 */

class SearchManager {
  constructor() {
    this._container = document.querySelector('[data-search]');
    if (!this._container) return;

    this._mode = this._container.dataset.searchMode || 'dropdown';
    this._input = this._container.querySelector('[data-search-input]');
    this._results = this._container.querySelector('[data-search-results]');
    this._preSearch = this._container.querySelector('[data-search-pre-search]');
    this._clearBtn = this._container.querySelector('[data-search-clear]');
    this._dropdown = this._container.querySelector('[data-search-dropdown]');

    this._debounceTimer = null;
    this._DEBOUNCE_MS = 300;
    this._MIN_CHARS = 1;
    this._RESULTS_LIMIT = 30;

    this._onInput = this._onInput.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClear = this._onClear.bind(this);

    this._init();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    if (!this._input) return;

    this._input.addEventListener('input', this._onInput);
    this._input.addEventListener('keydown', this._onKeyDown);
    this._clearBtn?.addEventListener('click', this._onClear);

    if (this._mode === 'dropdown') {
      this._initDropdown();
    } else {
      this._initDrawer();
    }
  }

  // ─── Modo Dropdown ────────────────────────────────────────────────────────

  _initDropdown() {
    // Abre ao focar no input
    this._input.addEventListener('focus', () => {
      this._showDropdown();
      this._showPreSearch();
    });

    // Fecha ao clicar fora do container
    document.addEventListener('click', e => {
      if (!this._container.contains(e.target)) this._hideDropdown();
    });

    // Fecha ao abrir qualquer drawer
    window.eventBus?.on('drawer:open', () => this._hideDropdown());
  }

  _showDropdown() {
    this._dropdown?.removeAttribute('hidden');
    this._dropdown?.classList.add('is-open');
    this._input.setAttribute('aria-expanded', 'true');
  }

  _hideDropdown() {
    this._dropdown?.setAttribute('hidden', '');
    this._dropdown?.classList.remove('is-open');
    this._input.setAttribute('aria-expanded', 'false');
  }

  // ─── Modo Drawer ──────────────────────────────────────────────────────────

  _initDrawer() {
    window.eventBus?.on('drawer:open', ({ name }) => {
      if (name !== 'search') return;
      // Foca no input apenas em desktop (evita teclado virtual no mobile)
      if (window.innerWidth >= 750) {
        requestAnimationFrame(() => this._input?.focus());
      }
      this._showPreSearch();
    });

    // Limpa resultados ao fechar o drawer
    window.eventBus?.on('drawer:close', ({ name }) => {
      if (name !== 'search') return;
      this._clearResults();
      this._showPreSearch();
      this._toggleClear(false);
      if (this._input) this._input.value = '';
    });
  }

  // ─── Busca ────────────────────────────────────────────────────────────────

  _onInput() {
    const query = this._input.value.trim();
    clearTimeout(this._debounceTimer);
    this._toggleClear(query.length > 0);

    if (query.length < this._MIN_CHARS) {
      this._clearResults();
      this._showPreSearch();
      return;
    }

    this._debounceTimer = setTimeout(() => this._fetchResults(query), this._DEBOUNCE_MS);
  }

  _buildSuggestUrl(q) {
    // Removido 'body' (descrição) dos fields para focar em matches diretos e evitar produtos aleatórios
    const fields = 'title,product_type,variants.title,vendor,tag,variants.sku';
    return (
      `/search/suggest.json?q=${encodeURIComponent(q)}` +
      `&resources[type]=product` +
      `&resources[limit]=${this._RESULTS_LIMIT}` +
      `&resources[options][prefix]=last` +
      `&resources[options][fields]=${fields}`
    );
  }

  _getQueryVariants(query) {
    const trimmed = query.trim();
    const variants = new Set([trimmed]);

    // Variante sem espaços: "5 th" → "5th"
    const noSpaces = trimmed.replace(/\s+/g, '');
    if (noSpaces !== trimmed) variants.add(noSpaces);

    // Variante com hífen: "5 th" → "5-th"
    const hyphenated = trimmed.replace(/\s+/g, '-');
    if (hyphenated !== trimmed) variants.add(hyphenated);

    // Palavras individuais foram removidas para evitar resultados aleatórios

    return [...variants];
  }

  // ─── Relevância ───────────────────────────────────────────────────────────

  _getScore(title, q, words, exactWordRegex) {
    const t = (title || '').toLowerCase();
    if (t === q) return 0;
    if (exactWordRegex.test(t)) return 0; // 1. Palavra inteira exata
    if (t.startsWith(q)) return 1; // 2. Começa com
    if (t.includes(q)) return 2; // 3. Contém a query
    if (words.every(w => t.includes(w))) return 3; // 4. Contém todas separadas
    const matchCount = words.filter(w => t.includes(w)).length;
    if (matchCount > 0) return 10 - matchCount; // 5. Contém algumas
    return 10; // 6. Aleatório (sem match no título)
  }

  _sortByRelevance(products, query) {
    const q = query.trim().toLowerCase();
    const words = q.split(/\s+/).filter(Boolean);
    const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const exactWordRegex = new RegExp(`(^|\\s|\\W)${escapedQ}($|\\s|\\W)`);

    return [...products].sort((a, b) => {
      const scoreA = this._getScore(a.title, q, words, exactWordRegex);
      const scoreB = this._getScore(b.title, q, words, exactWordRegex);
      return scoreA - scoreB;
    });
  }

  async _fetchSuggestResults(q) {
    const res = await fetch(this._buildSuggestUrl(q));
    if (!res.ok) return [];
    const data = await res.json();
    return data.resources?.results?.products || [];
  }

  // ─── Busca complementar via página (scrape HTML + JS Fetch) ───────────────

  _normalizeJsProduct(p) {
    const firstVariant = p.variants?.find(v => v.available) || p.variants?.[0];
    return {
      handle: p.handle,
      title: p.title,
      url: `/products/${p.handle}`,
      price: firstVariant ? (firstVariant.price / 100).toString() : (p.price_min / 100).toString(),
      price_min: p.price_min / 100,
      price_max: p.price_max / 100,
      featured_image: p.featured_image ? { url: p.featured_image } : null,
      _fromPage: true,
    };
  }

  async _fetchProductByHandle(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return this._normalizeJsProduct(await res.json());
    } catch { return null; }
  }

  async _fetchSearchPageProducts(query) {
    try {
      // Adiciona o wildcard para garantir que ele ache todos os produtos com a palavra
      const searchQ = encodeURIComponent(query.trim() + '*');
      const res = await fetch(`/search?type=product&q=${searchQ}&limit=50`);
      if (!res.ok) return [];
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const handles = [];
      const seen = new Set();
      doc.querySelectorAll('a[href*="/products/"]').forEach(a => {
        const m = a.pathname.match(/^\/products\/([^/?#]+)$/);
        if (m && !seen.has(m[1])) {
          seen.add(m[1]);
          handles.push(m[1]);
        }
      });

      if (!handles.length) return [];

      const results = await Promise.all(
        handles.slice(0, this._RESULTS_LIMIT).map(h => this._fetchProductByHandle(h))
      );
      return results.filter(Boolean);
    } catch (e) {
      console.error('[SearchManager] Scrape failed:', e);
      return [];
    }
  }

  async _fetchResults(query) {
    const q = query.trim().toLowerCase();
    this._currentQuery = q; // Rastreia a query atual para evitar que buscas antigas sobrescrevam as novas

    try {
      const words = q.split(/\s+/).filter(Boolean);
      const escapedQ = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const exactWordRegex = new RegExp(`(^|\\s|\\W)${escapedQ}($|\\s|\\W)`);

      const variants = this._getQueryVariants(query);

      // --- ETAPA 1: Busca Rápida (Suggest.json) ---
      // Dispara busca na API nativa que é instantânea
      const suggestLists = await Promise.all(variants.map(v => this._fetchSuggestResults(v)));

      const seen = new Set();
      let products = [];
      for (const list of suggestLists) {
        for (const p of list) {
          if (!seen.has(p.handle)) {
            seen.add(p.handle);
            products.push(p);
          }
        }
      }

      // Se a query mudou enquanto buscava, aborta
      if (this._currentQuery !== q) return;

      // Renderiza IMEDIATAMENTE os resultados rápidos para o usuário não sentir lentidão
      let currentProducts = this._sortByRelevance([...products], query);
      
      // Busca preços dos rápidos
      currentProducts = await Promise.all(currentProducts.map(async (p) => {
        try {
          if (p._fromPage || p.price_min === p.price_max) return p;
          const detailRes = await fetch(`/products/${p.handle}.js`);
          if (detailRes.ok) {
            const detail = await detailRes.json();
            const firstAvailable = detail.variants.find(v => v.available) || detail.variants[0];
            if (firstAvailable) {
              p.price = (firstAvailable.price / 100).toString();
            }
          }
        } catch (e) {}
        return p;
      }));

      if (this._currentQuery !== q) return;
      this._renderResults(currentProducts);

      // --- ETAPA 2: Busca Profunda (Scrape HTML) ---
      // Complementa com a busca mais pesada em background
      if (products.length < this._RESULTS_LIMIT) {
        const pageProducts = await this._fetchSearchPageProducts(query);
        
        if (this._currentQuery !== q) return; // Aborta se o usuário já digitou outra coisa

        let addedNew = false;
        for (const p of pageProducts) {
          if (!seen.has(p.handle)) {
            seen.add(p.handle);
            products.push(p);
            addedNew = true;
          }
        }

        if (addedNew) {
          products = this._sortByRelevance(products, query);

          // Joga fora apenas os totalmente aleatórios se já tivermos resultados suficientes
          const strictProducts = products.filter(p => this._getScore(p.title, q, words, exactWordRegex) < 10);
          if (strictProducts.length > 0) {
            products = strictProducts;
          }

          products = products.slice(0, this._RESULTS_LIMIT);

          // A etapa 2 já traz os preços corretos de _normalizeJsProduct, então basta renderizar
          this._renderResults(products);
        }
      }
    } catch (err) {
      console.error('[SearchManager] Fetch failed:', err);
    }
  }

  // ─── Renderização ─────────────────────────────────────────────────────────

  _renderResults(products) {
    this._hidePreSearch();
    if (!this._results) return;

    if (products.length === 0) {
      this._results.innerHTML = `<p class="search-manager__empty" role="status">Nenhum resultado encontrado.</p>`;
      return;
    }

    this._results.innerHTML = products.map(p => this._renderCard(p)).join('');

    // Navegação por teclado nos resultados
    const items = [...this._results.querySelectorAll('[data-search-result]')];
    items.forEach((el, i) => {
      el.addEventListener('keydown', e => {
        if (e.key === 'ArrowDown') { e.preventDefault(); items[i + 1]?.focus() ?? items[0]?.focus(); }
        if (e.key === 'ArrowUp') { e.preventDefault(); i === 0 ? this._input.focus() : items[i - 1]?.focus(); }
        if (e.key === 'Escape') { this._clearInput(); }
      });
    });
  }

  _renderCard(product) {
    const price = product.price
      ? Number(product.price).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : '';
    const image = product.featured_image?.url || '';

    return `
      <a href="${product.url}"
         data-search-result
         class="search-manager__result"
         tabindex="0">
        ${image
        ? `<img class="search-manager__result-image"
                  src="${image}"
                  alt="${product.title}"
                  width="60" height="60"
                  loading="lazy">`
        : ''}
        <div class="search-manager__result-info">
          <span class="search-manager__result-title">${product.title}</span>
          ${price ? `<span class="search-manager__result-price">${price}</span>` : ''}
        </div>
      </a>
    `;
  }

  // ─── Estado de UI ─────────────────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const first = this._results?.querySelector('[data-search-result]');
      first?.focus();
    }
    if (e.key === 'Escape') {
      this._clearInput();
      if (this._mode === 'dropdown') this._hideDropdown();
      else window.DrawerManager?.close('search');
    }
  }

  _onClear() {
    this._clearInput();
    this._input.focus();
  }

  _clearInput() {
    if (this._input) this._input.value = '';
    this._clearResults();
    this._showPreSearch();
    this._toggleClear(false);
  }

  _clearResults() {
    if (this._results) this._results.innerHTML = '';
  }

  _showPreSearch() {
    this._preSearch?.removeAttribute('hidden');
  }

  _hidePreSearch() {
    this._preSearch?.setAttribute('hidden', '');
  }

  _toggleClear(show) {
    if (!this._clearBtn) return;
    this._clearBtn.hidden = !show;
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  open() {
    if (this._mode === 'drawer') window.DrawerManager?.open('search');
    else this._showDropdown();
  }

  close() {
    if (this._mode === 'drawer') window.DrawerManager?.close('search');
    else this._hideDropdown();
  }

  toggle() {
    if (this._mode === 'drawer') window.DrawerManager?.toggle('search');
    else this._dropdown?.classList.contains('is-open') ? this._hideDropdown() : this._showDropdown();
  }

  destroy() {
    this._input?.removeEventListener('input', this._onInput);
    this._input?.removeEventListener('keydown', this._onKeyDown);
    clearTimeout(this._debounceTimer);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.SearchManager = new SearchManager();
});
